import { BDE, CVL, HARD, HAUTS_DE_FRANCE, KARNA, KFET, PERSONNE, ROUEN, SOFT, STRASBOURG, TECKOS } from "@overbookd/team-code";
import { parsePhoneNumber } from "awesome-phonenumber";
import { jwtDecode } from "jwt-decode";
import { Edition, ONE_SECOND_IN_MS, formatDateWithExplicitMonthAndDay } from "@overbookd/time";
import { REGISTER_URL } from "@overbookd/web-page";
// src/registration.constant.ts
const readableVolunteerCommitmentPresentation = `
<p>Hello et merci d'être venu·e jusqu'ici ! 🤗</p>
<p>
Le festival des 24 heures de l'INSA n'est possible que grâce aux plus
de <strong>400 bénévoles</strong> qui, comme toi, ont accepté de venir
nous donner un coup de main pour préparer, organiser et faire en
sorte que les festivaliers passent le meilleur moment possible. 🎉
</p>
<p>
Pour que l'on puisse t'accueillir dans les meilleures conditions il
nous faut cependant quelques informations sur toi et le temps que tu
souhaites consacrer à nous aider.
</p>
<p>
Si tu as la moindre question sur le festival et ce que tu peux faire
pour nous aider, n'hésite pas a contacter les
<a href="mailto:humains@24heures.org">responsables bénévoles</a>.
Ils seront tes principaux interlocuteurs en amont et en aval du
festival. 😎
</p>`;
const readableStaffCommitmentPresentation = `
<p>Hello toi ! Merci d'être venu·e jusqu'ici et bienvenue parmi nous ! 👋</p>
<p>
Si tu as atterri sur cette page (de ton plein gré ou par traquenard,
on ne juge pas), c'est que tu as pris une super décision : t'engager
dans cette incroyable aventure que sont les 24 heures de l'INSA ! 🎉
</p>
<p>
Pour que l'on puisse organiser au mieux ce festival avec notre centaine
de bénévoles engagés à l'année, nous avons besoin de quelques informations
sur toi. On te présente donc <strong>Overbookd</strong> qui sera ton
meilleur acolyte toute l'année ! On te laisse t'inscrire en suivant les étapes…
</p>
<p>
Si tu as la moindre question, n'hésite pas a contacter le·a
<a href="mailto:secretaire.general@24heures.org">secrétaire général·e</a>. 😎
</p>
<p>Que l'aventure commence ! 🔥</p>`;
const defaultVolunteerCommitmentPresentation = readableVolunteerCommitmentPresentation.replace(/\n/g, " ");
const defaultStaffCommitmentPresentation = readableStaffCommitmentPresentation.replace(/\n/g, " ");
// src/register-form/fulfilled-registration.ts
const TEAM_CODES = [
	BDE,
	STRASBOURG,
	KFET,
	KARNA,
	TECKOS,
	HAUTS_DE_FRANCE,
	CVL,
	ROUEN
];
function isTeamCode(code) {
	return TEAM_CODES.includes(code);
}
const accountStatuses = {
	EXISTING: "EXISTING",
	NEW: "NEW"
};
function isNewAccountRegistration(registration) {
	return registration.status === accountStatuses.NEW;
}
// src/newcomer.ts
function isJoinableTeams(teams) {
	const maxTwoTeams = teams.length <= 2;
	const onlyJoinableTeams = teams.every((team) => TEAM_CODES.some((code) => code === team));
	return maxTwoTeams && onlyJoinableTeams;
}
const STAFF = "STAFF";
const VOLUNTEER = "VOLUNTEER";
function isStaffRegistered(registree) {
	return registree.membership === STAFF;
}
function isVolunteerRegistered(registree) {
	return registree.membership === VOLUNTEER;
}
// src/register-form/fields/email-field.ts
var EmailField = class EmailField {
	email;
	emailPattern = /* @__PURE__ */ new RegExp("^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,4}$");
	insaEmailPattern = /* @__PURE__ */ new RegExp("^((?!insa-lyon.fr).)*$");
	validEmail = {
		test: (value) => this.emailPattern.test(value),
		reason: "Adresse mail non valable"
	};
	nonInsaEmail = {
		test: (value) => this.insaEmailPattern.test(value),
		reason: "Pas d'adresse insa 🙏"
	};
	constructor(email) {
		this.email = email;
	}
	static build(email) {
		return new EmailField(email.trim());
	}
	get value() {
		return this.email.toLowerCase();
	}
	get rules() {
		return [this.validEmail, this.nonInsaEmail];
	}
	get isValid() {
		return this.rules.every((rule) => rule.test(this.email));
	}
	get reasons() {
		return this.rules.filter((rule) => !rule.test(this.email)).map(({ reason }) => reason);
	}
};
// src/register-form/fields/first-name-field.ts
var FirstNameField = class FirstNameField {
	firstName;
	constructor(firstName) {
		this.firstName = firstName;
	}
	get value() {
		return this.firstName;
	}
	get isValid() {
		return this.firstName.length > 0;
	}
	get reasons() {
		return this.isValid ? [] : ["Il faut renseigner un prenom"];
	}
	static build(firstName) {
		return new FirstNameField(firstName.trim());
	}
};
// src/register-form/fields/last-name-field.ts
var LastNameField = class LastNameField {
	lastName;
	constructor(lastName) {
		this.lastName = lastName;
	}
	get value() {
		return this.lastName;
	}
	get isValid() {
		return this.lastName.length > 0;
	}
	get reasons() {
		return this.isValid ? [] : ["Il faut renseigner un nom"];
	}
	static build(lastName) {
		return new LastNameField(lastName.trim());
	}
};
// src/register-form/fields/password-field.ts
const SPECIAL_CHARS_REGEX_PATTERN = "[!@#$%^&*+=_.,;:?{}()/|\\-]";
var PasswordField = class PasswordField {
	password;
	accountStatus;
	minusculePattern = /* @__PURE__ */ new RegExp("[a-z]");
	majusculePattern = /* @__PURE__ */ new RegExp("[A-Z]");
	numberPattern = /* @__PURE__ */ new RegExp("[0-9]");
	specialCharPattern = new RegExp(SPECIAL_CHARS_REGEX_PATTERN);
	minPasswordLength = 12;
	containsMinuscule = {
		test: (value) => this.minusculePattern.test(value),
		reason: "Il faut au moins une minuscule dans le mot de passe."
	};
	containsMajuscule = {
		test: (value) => this.majusculePattern.test(value),
		reason: "Il faut au moins une MAJUSCULE dans le mot de passe."
	};
	containsNumber = {
		test: (value) => this.numberPattern.test(value),
		reason: "Il faut au moins un chiffre dans le mot de passe."
	};
	containsSpecialChar = {
		test: (value) => this.specialCharPattern.test(value),
		reason: "Il faut au moins un caractère spécial (!@#$%^&*=+_{}[]()|.) dans le mot de passe."
	};
	atLeast8CharactersLong = {
		test: (value) => value.length >= this.minPasswordLength,
		reason: `Il faut au moins ${this.minPasswordLength} caractères dans le mot de passe.`
	};
	constructor(password, accountStatus) {
		this.password = password;
		this.accountStatus = accountStatus;
	}
	static build(password, accountStatus) {
		return new PasswordField(password, accountStatus);
	}
	get value() {
		return this.accountStatus === accountStatuses.NEW ? this.password : void 0;
	}
	get rules() {
		return [
			this.containsMinuscule,
			this.containsMajuscule,
			this.containsNumber,
			this.containsSpecialChar,
			this.atLeast8CharactersLong
		];
	}
	get isValid() {
		if (this.accountStatus === accountStatuses.EXISTING) return true;
		return this.rules.every((rule) => rule.test(this.password));
	}
	get reasons() {
		if (this.accountStatus === accountStatuses.EXISTING) return [];
		return this.rules.filter((rule) => !rule.test(this.password ?? "")).map(({ reason }) => reason);
	}
};
// src/phone-number/phone-number.ts
const FRENCH_REGION_CODE = "FR";
function parseNumber(phoneNumber) {
	return parsePhoneNumber(phoneNumber, { regionCode: FRENCH_REGION_CODE });
}
function isFrenchNumber(parsedNumber) {
	return parsedNumber.regionCode === FRENCH_REGION_CODE;
}
function buildWhatsAppLink(phoneNumber) {
	return `https://wa.me/${phoneNumber}`;
}
function isMobilePhoneNumberValid(phoneNumber) {
	const parsedNumber = parseNumber(phoneNumber);
	return parsedNumber.valid && parsedNumber.typeIsMobile;
}
function isPhoneNumberValid(phoneNumber) {
	return parseNumber(phoneNumber).valid;
}
function formatPhoneNumber(phoneNumber) {
	const parsedNumber = parseNumber(phoneNumber);
	if (!parsedNumber.valid) return phoneNumber.trim();
	return isFrenchNumber(parsedNumber) ? parsedNumber.number.national : parsedNumber.number.international;
}
function formatPhoneNumberToInternational(phoneNumber) {
	const parsedNumber = parseNumber(phoneNumber);
	if (!parsedNumber.valid) return phoneNumber.trim();
	return parsedNumber.number.e164;
}
function formatPhoneLink(phoneNumber) {
	const parsedNumber = parseNumber(phoneNumber);
	if (!parsedNumber.valid) return `tel:${phoneNumber.trim()}`;
	return isFrenchNumber(parsedNumber) ? parsedNumber.number.rfc3966 : buildWhatsAppLink(parsedNumber.number.e164);
}
function formatEmailLink(email) {
	return `mailto:${email}`;
}
// src/register-form/fields/mobile-phone-field.ts
var MobilePhoneField = class MobilePhoneField {
	mobilePhone;
	constructor(mobilePhone) {
		this.mobilePhone = mobilePhone;
	}
	get value() {
		return this.mobilePhone;
	}
	get isValid() {
		return isMobilePhoneNumberValid(this.mobilePhone);
	}
	get reasons() {
		return this.isValid ? [] : ["Numéro de téléphone mobile non valable"];
	}
	static build(mobilePhone) {
		const internationalMobilePhone = formatPhoneNumberToInternational(mobilePhone);
		return new MobilePhoneField(internationalMobilePhone);
	}
};
// src/register-form/fields/nickname-field.ts
var NicknameField = class NicknameField {
	nickname;
	constructor(nickname) {
		this.nickname = nickname;
	}
	get value() {
		return this.nickname;
	}
	get isValid() {
		return this.nickname === void 0 ? true : this.nickname.length > 0;
	}
	get reasons() {
		return this.isValid ? [] : ["Il faut renseigner un surnom"];
	}
	static build(nickname) {
		const cleanedNickname = nickname?.trim();
		if (!cleanedNickname) return new NicknameField(void 0);
		return new NicknameField(cleanedNickname);
	}
};
// src/register-form/fields/birth-date-field.ts
var BirthDateField = class BirthDateField {
	birthDate;
	minimumBirthDate = /* @__PURE__ */ new Date("1950-01-01");
	notBefore1950 = {
		test: (value) => value.getTime() > this.minimumBirthDate.getTime(),
		reason: "Tu n'es pas si vieux !"
	};
	notInFuture = {
		test: (value) => value.getTime() < Date.now(),
		reason: "Tu ne peux pas naître dans le futur 🕵️‍♂️"
	};
	constructor(birthDate) {
		this.birthDate = birthDate;
	}
	static build(birthDate) {
		return new BirthDateField(birthDate);
	}
	get value() {
		return this.birthDate;
	}
	get rules() {
		return [this.notBefore1950, this.notInFuture];
	}
	get isValid() {
		return this.rules.every((rule) => rule.test(this.birthDate));
	}
	get reasons() {
		return this.rules.filter((rule) => !rule.test(this.birthDate)).map(({ reason }) => reason);
	}
};
// src/register-form/fields/comment-field.ts
var CommentField = class CommentField {
	comment;
	constructor(comment) {
		this.comment = comment;
	}
	get value() {
		return this.comment;
	}
	get isValid() {
		return this.comment === void 0 ? true : this.comment.length > 0;
	}
	get reasons() {
		return this.isValid ? [] : ["Il faut préciser ton commentaire"];
	}
	static build(comment) {
		const cleanedComment = comment?.trim();
		if (!cleanedComment) return new CommentField(void 0);
		return new CommentField(cleanedComment);
	}
};
// src/register-form/fields/teams-field.ts
var TeamsField = class TeamsField {
	teams;
	constructor(teams) {
		this.teams = teams;
	}
	get value() {
		return this.teams;
	}
	MAX_TEAM_ALLOWED = 2;
	get isValid() {
		return this.teams.length <= this.MAX_TEAM_ALLOWED;
	}
	get reasons() {
		return this.isValid ? [] : [`Tu ne peux pas rejoindre plus de ${this.MAX_TEAM_ALLOWED} équipes`];
	}
	static build(teams) {
		return new TeamsField(teams);
	}
};
// src/register-form/fields/EULA-field.ts
var EULAField = class EULAField {
	hasApprovedEULA;
	constructor(hasApprovedEULA) {
		this.hasApprovedEULA = hasApprovedEULA;
	}
	get value() {
		return this.hasApprovedEULA;
	}
	get isValid() {
		return this.hasApprovedEULA === true;
	}
	get reasons() {
		return this.isValid ? [] : ["Les Condidtions Générales d'Utilisation doivent être approuvées"];
	}
	static build(hasApprovedEULA) {
		return new EULAField(hasApprovedEULA ?? false);
	}
};
// src/register-form/fields/volunteer-charter-field.ts
var VolunteerCharterField = class VolunteerCharterField {
	membership;
	hasApprovedVolunteerCharter;
	constructor(membership, hasApprovedVolunteerCharter) {
		this.membership = membership;
		this.hasApprovedVolunteerCharter = hasApprovedVolunteerCharter;
	}
	get value() {
		return this.hasApprovedVolunteerCharter;
	}
	get isValid() {
		if (!shouldSignVolunteerCharter(this.membership)) return true;
		return this.hasApprovedVolunteerCharter === true;
	}
	get reasons() {
		return this.isValid ? [] : ["La Charte Bénévole doit être approuvée"];
	}
	static build(membership, hasApprovedVolunteerCharter) {
		return new VolunteerCharterField(membership, hasApprovedVolunteerCharter ?? false);
	}
};
// src/register-form/registration.error.ts
var RegistrationError = class extends Error {
	reasons;
	constructor(reasons, errorMessage = "Erreur lors de l'inscription") {
		super(`${errorMessage}:\n${reasons.join("\n")}`);
		this.reasons = reasons;
	}
};
var NotFulfilledRegistration = class extends RegistrationError {
	constructor(reasons) {
		super(reasons, "L'inscription n'est pas complète");
	}
};
// src/register-form/register-form.ts
var RegisterForm = class RegisterForm {
	membership;
	accountStatus;
	data;
	email;
	firstName;
	lastName;
	password;
	mobilePhone;
	nickname;
	birthDate;
	comment;
	teams;
	EULA;
	volunteerCharter;
	constructor(membership, accountStatus, data) {
		this.membership = membership;
		this.accountStatus = accountStatus;
		this.data = data;
		this.email = EmailField.build(data.email ?? "");
		this.firstName = FirstNameField.build(data.firstName ?? "");
		this.lastName = LastNameField.build(data.lastName ?? "");
		this.password = PasswordField.build(data.password, accountStatus);
		this.mobilePhone = MobilePhoneField.build(data.mobilePhone ?? "");
		this.nickname = NicknameField.build(data.nickname);
		this.birthDate = BirthDateField.build(data.birthDate ?? /* @__PURE__ */ new Date("1949-12-25"));
		this.comment = CommentField.build(data.comment);
		this.teams = TeamsField.build(data.teams ?? []);
		this.EULA = EULAField.build(data.hasApprovedEULA);
		this.volunteerCharter = VolunteerCharterField.build(membership, data.hasSignedVolunteerCharter);
	}
	static initFor(membership, accountStatus) {
		return new RegisterForm(membership, accountStatus, {});
	}
	fillEmail(email) {
		return this.clone({ email });
	}
	clearEmail() {
		return this.clone({ email: void 0 });
	}
	fillFirstName(firstName) {
		return this.clone({ firstName });
	}
	clearFirstName() {
		return this.clone({ firstName: void 0 });
	}
	fillLastName(lastName) {
		return this.clone({ lastName });
	}
	clearLastName() {
		return this.clone({ lastName: void 0 });
	}
	fillPassword(password) {
		return this.clone({ password });
	}
	clearPassword() {
		return this.clone({ password: void 0 });
	}
	fillMobilePhone(mobilePhone) {
		return this.clone({ mobilePhone });
	}
	clearMobilePhone() {
		return this.clone({ mobilePhone: void 0 });
	}
	fillNickname(nickname) {
		return this.clone({ nickname });
	}
	clearNickname() {
		return this.clone({ nickname: void 0 });
	}
	fillBirthDate(birthDate) {
		return this.clone({ birthDate });
	}
	clearBirthDate() {
		return this.clone({ birthDate: void 0 });
	}
	fillComment(comment) {
		return this.clone({ comment });
	}
	clearComment() {
		return this.clone({ comment: void 0 });
	}
	fillTeams(teams) {
		return this.clone({ teams });
	}
	clearTeams() {
		return this.clone({ teams: [] });
	}
	approveEndUserLicenceAgreement() {
		return this.clone({ hasApprovedEULA: true });
	}
	denyEndUserLicenceAgreement() {
		return this.clone({ hasApprovedEULA: false });
	}
	signVolunteerCharter() {
		return this.clone({ hasSignedVolunteerCharter: true });
	}
	denyVolunteerCharter() {
		return this.clone({ hasSignedVolunteerCharter: false });
	}
	clone(patch) {
		return new RegisterForm(this.membership, this.accountStatus, {
			...this.data,
			...patch
		});
	}
	get currentRegistration() {
		const volunteerCharter = shouldSignVolunteerCharter(this.membership) ? { hasSignedVolunteerCharter: this.volunteerCharter.value } : {};
		const password = this.needsPassword ? { password: this.password.value } : {};
		return {
			status: this.accountStatus,
			email: this.email.value,
			firstName: this.firstName.value,
			lastName: this.lastName.value,
			mobilePhone: this.mobilePhone.value,
			nickname: this.nickname.value,
			birthDate: this.birthDate.value,
			comment: this.comment.value,
			teams: this.teams.value,
			hasApprovedEULA: this.EULA.value,
			...password,
			...volunteerCharter
		};
	}
	get fields() {
		return [
			this.email,
			this.firstName,
			this.lastName,
			this.password,
			this.mobilePhone,
			this.nickname,
			this.birthDate,
			this.comment,
			this.teams,
			this.EULA,
			this.volunteerCharter
		];
	}
	get isValid() {
		return this.fields.every((field) => field?.isValid);
	}
	get reasons() {
		return this.fields.flatMap((field) => field.reasons);
	}
	get needsPassword() {
		return this.accountStatus === accountStatuses.NEW;
	}
	complete() {
		if (!this.isFulfilled(this.currentRegistration)) throw new NotFulfilledRegistration(this.reasons);
		return this.currentRegistration;
	}
	isFulfilled(_registration) {
		return this.isValid;
	}
};
function shouldSignVolunteerCharter(membership) {
	return membership === VOLUNTEER;
}
// src/register-newcomer.ts
var RegisterNewcomer = class {
	newcomerRepository;
	constructor(newcomerRepository) {
		this.newcomerRepository = newcomerRepository;
	}
	async fromRegisterForm(form, membership) {
		const dataForm = RegisterForm.initFor(membership, form.status).fillEmail(form.email).fillFirstName(form.firstName).fillLastName(form.lastName).fillMobilePhone(form.mobilePhone).fillBirthDate(form.birthDate).fillTeams(form.teams);
		const withNickname = form.nickname !== void 0 ? dataForm.fillNickname(form.nickname) : dataForm.clearNickname();
		const withComment = form.comment !== void 0 ? withNickname.fillComment(form.comment) : withNickname.clearComment();
		const withPassword = isNewAccountRegistration(form) ? withComment.fillPassword(form.password) : withComment.clearPassword();
		const withEULA = form.hasApprovedEULA ? withPassword.approveEndUserLicenceAgreement() : withPassword.denyEndUserLicenceAgreement();
		const personalData = stripRegistrationData((form.hasSignedVolunteerCharter ? withEULA.signVolunteerCharter() : withEULA.denyVolunteerCharter()).complete());
		return {
			...await this.newcomerRepository.save(personalData),
			membership
		};
	}
};
function stripRegistrationData(registration) {
	if (isNewAccountRegistration(registration)) {
		const { status: _status, password: _password, ...withoutPassword } = registration;
		return withoutPassword;
	}
	const { status: _status, ...rest } = registration;
	return rest;
}
// src/enroll/joinable-team.ts
const joinableTeams = {
	SOFT,
	HARD
};
// src/enroll/enroll-candidates.ts
const CANDIDATE_ENROLLED = "candidate-enrolled";
var EnrollCandidates = class {
	memberships;
	events;
	constructor(memberships, events) {
		this.memberships = memberships;
		this.events = events;
	}
	async apply(enrolling) {
		await this.checkEnrolledInOtherTeam(enrolling);
		const toEnrollCandidates = await this.retrieveCandidatesToEnroll(enrolling);
		const { team } = enrolling;
		await this.memberships.join([team, PERSONNE]).as(toEnrollCandidates);
		this.generateEvents({
			candidates: toEnrollCandidates,
			team
		});
	}
	generateEvents({ candidates, team }) {
		candidates.map((candidate) => {
			const data = {
				candidate,
				team
			};
			this.events.publish({
				type: CANDIDATE_ENROLLED,
				data
			});
		});
	}
	async retrieveCandidatesToEnroll(enrolling) {
		const { team, candidates } = enrolling;
		const enrolledInSameTeam = await this.memberships.enrolledIn(team).among(candidates);
		return this.keepNotEnrolled(candidates).among(enrolledInSameTeam);
	}
	async checkEnrolledInOtherTeam(enrolling) {
		const { team, candidates } = enrolling;
		const otherTeam = team === HARD ? SOFT : HARD;
		const enrolledInOtherTeam = await this.memberships.enrolledIn(otherTeam).among(candidates);
		if (enrolledInOtherTeam.length === 0) return;
		throw new AlreadyEnrolledError(enrolledInOtherTeam);
	}
	keepNotEnrolled(candidates) {
		return { among: (enrolled) => candidates.filter((candidate) => enrolled.every(({ id }) => id !== candidate.id)) };
	}
};
var AlreadyEnrolledError = class extends Error {
	constructor(candidates) {
		const message = `${candidates.map(({ name }) => name).join(" and ")} est/sont déjà enrollés`;
		super(message);
	}
};
// src/forget-member/forget-member.error.ts
const DEFAULT_ERROR_MESSAGE = "Nous ne pouvons pas l'effacer d'Overbookd.\n";
const HAS_FUTURE_ASSIGNMENT_ERROR_MESSAGE = `${DEFAULT_ERROR_MESSAGE}Iel est affecté·e à une tâche à venir.`;
const IN_DEBT_ERROR_MESSAGE = `${DEFAULT_ERROR_MESSAGE}Iel a des dettes auprès de l'association.`;
const HAS_MONEY_ERROR_MESSAGE = `${DEFAULT_ERROR_MESSAGE}Iel a de l'argent auprès de l'association.`;
var ForgetMemberError = class extends Error {};
var HasFutureAssignment = class extends ForgetMemberError {
	constructor() {
		super(HAS_FUTURE_ASSIGNMENT_ERROR_MESSAGE);
	}
};
var HasOpenSharedMeal = class extends ForgetMemberError {
	sharedMealDates;
	constructor(sharedMealDates) {
		super(`${DEFAULT_ERROR_MESSAGE}Iel est inscrit·e à des repas partagés non cloturés: ${sharedMealDates.join(", ")}.`);
		this.sharedMealDates = sharedMealDates;
	}
};
var HasTask = class extends ForgetMemberError {
	taskIds;
	constructor(taskIds) {
		super(`${DEFAULT_ERROR_MESSAGE}Iel est affecté·e aux FT : #${taskIds.join(", #")}.`);
		this.taskIds = taskIds;
	}
};
var HasActivity = class extends ForgetMemberError {
	activityIds;
	constructor(activityIds) {
		super(`${DEFAULT_ERROR_MESSAGE}Iel est affecté·e aux FA : #${activityIds.join(", #")}.`);
		this.activityIds = activityIds;
	}
};
var InDebt = class extends ForgetMemberError {
	constructor() {
		super(IN_DEBT_ERROR_MESSAGE);
	}
};
var HasMoney = class extends ForgetMemberError {
	constructor() {
		super(HAS_MONEY_ERROR_MESSAGE);
	}
};
// src/forget-member/anonymous-member.ts
const ANONYMOUS = "Anonymous";
const ANONYMOUS_MOBILE_PHONE = "0600000000";
const ANONYMOUS_BIRTH_DATE = /* @__PURE__ */ new Date("1970-01-01");
// src/forget-member/forget-about.strategy.ts
var WithoutTransactions = class WithoutTransactions {
	id;
	members;
	constructor(id, members) {
		this.id = id;
		this.members = members;
	}
	static init({ id, repository }) {
		return new WithoutTransactions(id, repository);
	}
	async forget() {
		await this.members.delete(this.id);
	}
};
var WithTransactions = class WithTransactions {
	id;
	members;
	constructor(id, members) {
		this.id = id;
		this.members = members;
	}
	static init({ id, repository }) {
		return new WithTransactions(id, repository);
	}
	forget() {
		const anonymizedMember = {
			firstName: ANONYMOUS,
			lastName: ANONYMOUS,
			mobilePhone: ANONYMOUS_MOBILE_PHONE,
			email: `anonymous+${this.id}@24heures.org`,
			birthDate: ANONYMOUS_BIRTH_DATE,
			comment: null,
			nickname: null,
			note: null,
			profilePicture: null,
			oidcId: null
		};
		return this.members.anonymize(this.id, anonymizedMember);
	}
};
// src/forget-member/forget-member.ts
var ForgetMember = class {
	members;
	constructor(members) {
		this.members = members;
	}
	async apply(id) {
		const [hasFutureAssignments, activityIds, taskIds, hasDebts, hasMoney, openSharedMealDates] = await Promise.all([
			this.members.hasFutureAssignments(id),
			this.members.activityIds(id),
			this.members.taskIds(id),
			this.members.hasDebts(id),
			this.members.hasMoney(id),
			this.members.openSharedMealDates(id)
		]);
		if (hasFutureAssignments) throw new HasFutureAssignment();
		if (activityIds.length > 0) throw new HasActivity(activityIds);
		if (taskIds.length > 0) throw new HasTask(taskIds);
		if (openSharedMealDates.length > 0) throw new HasOpenSharedMeal(openSharedMealDates);
		if (hasDebts) throw new InDebt();
		if (hasMoney) throw new HasMoney();
		const strategy = await this.members.hasTransactions(id) ? WithTransactions : WithoutTransactions;
		const strategyInitializer = {
			id,
			repository: this.members
		};
		return strategy.init(strategyInitializer).forget();
	}
	async shouldAnonymize(id) {
		return await this.members.hasTransactions(id);
	}
};
// src/invite-staff/invite-staff.ts
const TOKEN = "token";
const LINK_EXPIRED = "Le lien a expiré";
const JWT_EXPIRES_IN = "30d";
var InviteStaff = class InviteStaff {
	static byLink({ domain, token }) {
		const baseUrl = new URL(`https://${domain}${REGISTER_URL}`);
		baseUrl.searchParams.append(TOKEN, token);
		return baseUrl;
	}
	static isTokenExpired(token) {
		try {
			const { exp } = jwtDecode(token);
			const expirationInMs = exp * ONE_SECOND_IN_MS;
			if (InviteStaff.isPast(expirationInMs)) return LINK_EXPIRED;
			return `Le lien expire le ${formatDateWithExplicitMonthAndDay(expirationInMs)}`;
		} catch {
			return LINK_EXPIRED;
		}
	}
	static isLinkExpired(link) {
		const token = link.searchParams.get(TOKEN);
		if (!token) return LINK_EXPIRED;
		return InviteStaff.isTokenExpired(token);
	}
	static isPast(expirationInMs) {
		return expirationInMs < Date.now();
	}
};
// src/membership-application/candidature.error.ts
function readableMembership(membership) {
	return membership === "STAFF" ? "orga" : "bénévole";
}
var MembershipApplicationError = class extends Error {};
var AlreadyCandidate = class extends MembershipApplicationError {
	membership;
	constructor(membership) {
		super(`Tu as déjà candidaté pour être ${readableMembership(membership)} pour cette édition`);
		this.membership = membership;
	}
};
var AlreadyCandidateForMembership = class extends MembershipApplicationError {
	membership;
	constructor(membership) {
		super(`Le·a candidat·e a déjà candidaté pour être ${readableMembership(membership)} pour cette édition`);
		this.membership = membership;
	}
};
var AlreadyRejected = class extends MembershipApplicationError {
	membership;
	constructor(membership) {
		super(`Le·a candidat·e a déjà été rejeté·e pour être ${readableMembership(membership)} pour cette édition`);
		this.membership = membership;
	}
};
var NotRejected = class extends MembershipApplicationError {
	membership;
	constructor(membership) {
		super(`Le·a candidat·e n'a pas été rejeté·e pour être ${readableMembership(membership)} pour cette édition`);
		this.membership = membership;
	}
};
// src/membership-application/apply-for.ts
var ApplyFor = class {
	candidates;
	constructor(candidates) {
		this.candidates = candidates;
	}
	async staff(id) {
		const edition = Edition.current;
		if (await this.candidates.isCandidate(id, edition, "STAFF")) throw new AlreadyCandidate(STAFF);
		const newCandidate = {
			id,
			membership: STAFF,
			edition,
			isRejected: false,
			candidatedAt: /* @__PURE__ */ new Date()
		};
		return this.candidates.add(newCandidate);
	}
	async volunteer(id) {
		const edition = Edition.current;
		if (await this.candidates.isCandidate(id, edition, "VOLUNTEER")) throw new AlreadyCandidate(VOLUNTEER);
		const newCandidate = {
			id,
			membership: VOLUNTEER,
			edition,
			isRejected: false,
			candidatedAt: /* @__PURE__ */ new Date()
		};
		return this.candidates.add(newCandidate);
	}
};
// src/membership-application/reject.ts
var RejectMembershipApplication = class {
	candidates;
	constructor(candidates) {
		this.candidates = candidates;
	}
	async applyOne(id, membership) {
		const edition = Edition.current;
		if (await this.candidates.isRejected(id, edition, membership)) throw new AlreadyRejected(membership);
		return this.candidates.reject(id, edition, membership);
	}
	async unapplyOne(id, membership) {
		const edition = Edition.current;
		if (!await this.candidates.isRejected(id, edition, membership)) throw new NotRejected(membership);
		return this.candidates.cancelRejection(id, edition, membership);
	}
};
// src/membership-application/switch.ts
var SwitchMembershipApplication = class {
	candidates;
	constructor(candidates) {
		this.candidates = candidates;
	}
	async applyOne(id, membership, newMembership) {
		const edition = Edition.current;
		if (await this.candidates.isCandidate(id, edition, newMembership)) throw new AlreadyCandidateForMembership(newMembership);
		await this.candidates.switchApplicationMembership(id, edition, membership, newMembership);
		return this.candidates.cancelRejection(id, edition, newMembership);
	}
};
export { ANONYMOUS, ANONYMOUS_MOBILE_PHONE, AlreadyCandidate, ApplyFor, CANDIDATE_ENROLLED, EnrollCandidates, ForgetMember, ForgetMemberError, InviteStaff, JWT_EXPIRES_IN, LINK_EXPIRED, MembershipApplicationError, TEAM_CODES as REGISTRATION_TEAM_CODES, RegisterForm, RegisterNewcomer, RegistrationError, RejectMembershipApplication, SPECIAL_CHARS_REGEX_PATTERN, STAFF, SwitchMembershipApplication, VOLUNTEER, defaultStaffCommitmentPresentation, defaultVolunteerCommitmentPresentation, formatEmailLink, formatPhoneLink, formatPhoneNumber, formatPhoneNumberToInternational, isJoinableTeams, isMobilePhoneNumberValid, isNewAccountRegistration, isPhoneNumberValid, isTeamCode as isRegistrationTeamCode, isStaffRegistered, isVolunteerRegistered, joinableTeams, accountStatuses as registrationAccountStatuses, shouldSignVolunteerCharter };

//# sourceMappingURL=index.mjs.map